// What a reply or an event carries over the wire: the value reduced to plain
// data. Functions are dropped, Map and Set become arrays, a cycle is cut
// with a marker, a class instance keeps its own enumerable fields, a long
// string is cut with a note, and the whole payload is capped. Pure, so the
// bridge never sends a value JSON cannot carry and never hangs on one.

export interface SerializeLimits {
  /** Characters a string keeps before it is cut. */
  maxString: number;
  /** Characters of JSON the whole payload may take. */
  maxBytes: number;
  /** Nesting levels kept; deeper values become a marker. */
  maxDepth: number;
}

export const DEFAULT_LIMITS: SerializeLimits = { maxString: 100_000, maxBytes: 1_000_000, maxDepth: 40 };

export const CIRCULAR = '[Circular]';
export const TOO_DEEP = '[Too deep]';

/** The payload when the serialised value is over `maxBytes`: a note and the start of its JSON. */
export interface TruncatedPayload {
  truncated: true;
  note: string;
  bytes: number;
  limit: number;
  head: string;
}

/**
 * `value` as plain data within `limits`. Undefined stays undefined (JSON drops
 * it from objects, makes it null in arrays), so a function-valued field
 * disappears and a method result of nothing is a reply without `result`.
 */
export function serialize(value: unknown, limits: SerializeLimits = DEFAULT_LIMITS): unknown {
  const reduced = reduce(value, limits, []);
  if (reduced === undefined) return undefined;
  const json = JSON.stringify(reduced);
  if (json === undefined || json.length <= limits.maxBytes) return reduced;
  const payload: TruncatedPayload = {
    truncated: true,
    note: `payload of ${json.length} characters is over the ${limits.maxBytes} limit; the head follows`,
    bytes: json.length,
    limit: limits.maxBytes,
    head: json.slice(0, Math.min(limits.maxString, Math.floor(limits.maxBytes / 10))),
  };
  return payload;
}

function reduce(value: unknown, limits: SerializeLimits, ancestors: object[]): unknown {
  switch (typeof value) {
    case 'undefined':
    case 'function':
    case 'symbol':
      return undefined;
    case 'boolean':
      return value;
    case 'number':
      return Number.isFinite(value) ? value : String(value);
    case 'bigint':
      return `${value}n`;
    case 'string':
      return cutString(value, limits.maxString);
  }
  if (value === null) return null;
  const object = value as object;
  if (ancestors.includes(object)) return CIRCULAR;
  if (ancestors.length >= limits.maxDepth) return TOO_DEEP;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString();
  if (value instanceof RegExp) return String(value);
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: cutString(value.stack ?? '', limits.maxString) };
  }
  if (ArrayBuffer.isView(value)) return { type: value.constructor.name, byteLength: value.byteLength };
  if (value instanceof ArrayBuffer) return { type: 'ArrayBuffer', byteLength: value.byteLength };
  if (typeof Promise !== 'undefined' && value instanceof Promise) return '[Promise]';
  if (typeof WeakMap !== 'undefined' && value instanceof WeakMap) return '[WeakMap]';
  if (typeof WeakSet !== 'undefined' && value instanceof WeakSet) return '[WeakSet]';

  const inner = [...ancestors, object];
  const element = (v: unknown) => {
    const r = reduce(v, limits, inner);
    return r === undefined ? null : r;
  };
  if (Array.isArray(value)) return value.map(element);
  if (value instanceof Map) return [...value.entries()].map(([k, v]) => [element(k), element(v)]);
  if (value instanceof Set) return [...value].map(element);
  if (isDomNode(value)) return describeNode(value);

  // A plain object, or a class instance reduced to its own enumerable fields.
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(object)) {
    let field: unknown;
    try {
      field = (object as Record<string, unknown>)[key];
    } catch (e) {
      field = `[Getter threw: ${e instanceof Error ? e.message : String(e)}]`;
    }
    const r = reduce(field, limits, inner);
    if (r !== undefined) out[key] = r;
  }
  return out;
}

function cutString(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}… [truncated: ${text.length} characters, ${max} kept]`;
}

interface NodeLike {
  nodeType: number;
  nodeName: string;
  id?: string;
  className?: unknown;
}

function isDomNode(value: unknown): value is NodeLike {
  const v = value as Partial<NodeLike>;
  return typeof v.nodeType === 'number' && typeof v.nodeName === 'string';
}

function describeNode(node: NodeLike): unknown {
  const out: Record<string, unknown> = { node: node.nodeName };
  if (node.id) out.id = node.id;
  if (typeof node.className === 'string' && node.className) out.className = node.className;
  return out;
}
